import { decodeLdsDealPayload } from "../../../../utils/orders/decodeLdsDealPayload";
import { pickLdsRoomBoardBasisRaw, resolveVacationPackageRoomBoardBasisSsot } from "../../../../utils/vacationPackageBoardBasisSsot";
import { companyPackageApiRoomsTrace } from "../../utils/corporateFeTrace";
import { isTotalRateSource } from "./extractSupplierOccupancyRates";

function pickString(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}

/**
 * Room name from findExtendedDeals extendedDeal row (API field order).
 */
export function pickExtendedDealApiRoomName(row) {
  if (!row || typeof row !== "object") return null;
  return pickString(
    row.roomTypeName,
    row.RoomTypeName,
    row.boxName,
    row.BoxName,
    row.roomType,
    row.RoomType,
    row.roomName,
    row.RoomName,
    row.roomname,
    row.name,
  );
}

/**
 * Board basis raw string — row fields first, then hotelBoard inside ldsDealId token, then package hotel.
 */
export function pickExtendedDealApiBoardBasisRaw(row, packageData = null) {
  if (!row || typeof row !== "object") {
    return { raw: null, source: "none" };
  }

  const fromRow = pickLdsRoomBoardBasisRaw(row);
  if (fromRow) {
    return { raw: fromRow, source: "api.row.boardBasis" };
  }

  const ldsDealId = row.ldsDealId ?? row.LdsDealId ?? row.dealToken ?? null;
  const decoded = decodeLdsDealPayload(ldsDealId);
  if (decoded?.hotelBoard) {
    return { raw: decoded.hotelBoard, source: "api.ldsDealId.hotelBoard" };
  }

  const hotel =
    packageData?.fullDeal?.hotel ??
    packageData?.hotel ??
    packageData?.hotelInfo ??
    packageData?.rawPackage?.fullDeal?.hotel ??
    null;
  const fromHotel = pickLdsRoomBoardBasisRaw(hotel) ?? pickString(hotel?.board, hotel?.roombasis);
  if (fromHotel) {
    return { raw: fromHotel, source: "api.package.hotel.board" };
  }

  return { raw: null, source: "none" };
}

export function pickExtendedDealApiCurrency(row) {
  if (!row || typeof row !== "object") return null;
  const direct = pickString(row.currency, row.Currency);
  if (direct) return direct;

  const ldsDealId = row.ldsDealId ?? row.LdsDealId;
  if (!ldsDealId) return null;

  try {
    let s0 = String(ldsDealId).trim();
    if (!s0 || /^\d{8,}$/.test(s0)) return null;
    let b64 = s0.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const bin = typeof atob === "function" ? atob(b64) : "";
    if (!bin) return null;
    const parsed = JSON.parse(bin);
    return pickString(parsed?.currency, parsed?.Currency);
  } catch {
    return null;
  }
}

/**
 * Occupancy price from API row (totalFare / adultFare) — never inferred from package card total.
 */
export function pickExtendedDealApiOccupancyPrice(row) {
  if (!row || typeof row !== "object") return null;

  const roomSet = pickString(row.roomSet, row.RoomSet, row.room_set);
  const totalRaw = row.totalFare ?? row.TotalFare;
  const adultRaw = row.adultFare ?? row.AdultFare;
  const totalNum = Number(totalRaw);
  const adultNum = Number(adultRaw);

  let amount = null;
  let field = null;
  let pricingBasis = null;

  if (Number.isFinite(totalNum) && totalNum > 0) {
    amount = Math.round(totalNum * 100) / 100;
    field = "totalFare";
    pricingBasis = isTotalRateSource(row) ? "total" : "total";
  } else if (Number.isFinite(adultNum) && adultNum > 0) {
    amount = Math.round(adultNum * 100) / 100;
    field = "adultFare";
    pricingBasis = "per_person";
  }

  if (amount == null) return null;

  return {
    amount,
    field,
    pricingBasis,
    roomSet: roomSet ? String(roomSet).toUpperCase() : null,
    currency: pickExtendedDealApiCurrency(row),
    isSupplierRate: true,
  };
}

function summarizeApiRoomRow(row, index) {
  const name = pickExtendedDealApiRoomName(row);
  const board = pickExtendedDealApiBoardBasisRaw(row);
  const occupancy = pickExtendedDealApiOccupancyPrice(row);
  return {
    index,
    roomName: name,
    roomSet: row?.roomSet ?? row?.RoomSet ?? null,
    boardBasisRaw: board.raw,
    boardBasisSource: board.source,
    totalFare: row?.totalFare ?? row?.TotalFare ?? null,
    adultFare: row?.adultFare ?? row?.AdultFare ?? null,
    infantFare: row?.infantFare ?? row?.InfantFare ?? null,
    currency: pickExtendedDealApiCurrency(row),
    dealId: row?.dealId ?? row?.DealId ?? null,
    hotelAllocation: row?.hotelAllocation ?? null,
    occupancyPrice: occupancy,
  };
}

/**
 * Dev trace: full findExtendedDeals rooms payload + per-row field summary.
 */
export function traceCompanyPackageApiRooms({
  ldsDealId = null,
  responseData = null,
  extendedDealList = null,
  source = "findExtendedDeals",
} = {}) {
  const list = Array.isArray(extendedDealList)
    ? extendedDealList
    : Array.isArray(responseData?.extendedDeal)
      ? responseData.extendedDeal
      : responseData?.extendedDeal
        ? [responseData.extendedDeal]
        : [];

  companyPackageApiRoomsTrace({
    source,
    ldsDealId: ldsDealId != null ? String(ldsDealId) : null,
    responseTopLevelKeys:
      responseData && typeof responseData === "object" ? Object.keys(responseData) : [],
    extendedDealCount: list.length,
    rowSummaries: list.map((row, index) => summarizeApiRoomRow(row, index)),
    rawExtendedDeal: list,
    rawResponse: responseData,
  });
}

/**
 * Maps one findExtendedDeals row to corporate room-option display fields (API-only, no package total).
 */
export function mapExtendedDealApiRowToCorporateFields(row, packageData, meta = {}) {
  const roomName = pickExtendedDealApiRoomName(row);
  const boardPick = pickExtendedDealApiBoardBasisRaw(row, packageData);
  const boardSsot = resolveVacationPackageRoomBoardBasisSsot(
    boardPick.raw != null ? { boardBasis: boardPick.raw } : {},
    {
      packageId: meta.packageId ?? null,
      roomIndex: meta.roomIndex ?? null,
      roomName,
      source: boardPick.source,
      emitTrace: false,
    },
  );

  const occupancyPrice = pickExtendedDealApiOccupancyPrice(row);
  const currency = pickExtendedDealApiCurrency(row) ?? "ILS";

  return {
    roomName,
    boardBasis: boardSsot.rawBoardBasis || boardPick.raw || null,
    boardCode: boardSsot.boardCode || null,
    boardLabel: boardSsot.displayedBoardBasis || null,
    normalizedBoardBasis: boardSsot.normalizedBoardBasis || null,
    boardBasisSource: boardPick.source,
    currency,
    apiOccupancyPrice: occupancyPrice,
    raw: row,
  };
}

export function isExtendedDealApiSourcePath(sourcePath) {
  return String(sourcePath ?? "") === "extendedDealApiData";
}
